import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Nationalities } from '../../shared/models/nationalities/nationalities.model';
import { PageModel } from '../../shared/models/page.model';
import { CreateUser } from '../models/create-user.model';
import { ErrorResponse } from '../models/error-response.model';
import { UnblockUser } from '../models/unblock-user.model';
import { UpdateUser } from '../models/update-user.model';
import { UserFilterParams } from '../models/user-filter-params.model';
import { User } from '../models/user.model';
import { UtilsService } from '../services/utils.service';
import { UsersService } from './users.service';

@Injectable({
	providedIn: 'root',
})
export class UsersApiService {
	constructor(
		private httpClient: HttpClient,
		private usersService: UsersService,
		private utilsService: UtilsService
	) {}

	geNationalitiesList$(): Observable<Nationalities[]> {
		return this.httpClient.get<Nationalities[]>(this.getNationalitiesPath());
	}

	getUsersPage$(pageSize: number, currentPage: number, filterParams?: UserFilterParams): Observable<PageModel<User>> {
		return this.httpClient.get<PageModel<User>>(this.getBasePath(), {
			params: this.usersService.getUserParams(pageSize, currentPage, filterParams),
		});
	}

	getById$(id: number): Observable<User> {
		return this.httpClient.get<User>(this.getIdPath(id));
	}

	createUser$(user: CreateUser): Observable<{ userId: number } | ErrorResponse> {
		return this.httpClient.post<{ userId: number }>(this.getBasePath(), this.usersService.buildCreateUserModel(user)).pipe(
			catchError((response: HttpErrorResponse) => {
				return throwError(this.utilsService.buildErrorModel(response));
			})
		);
	}

	updateUser$(userId: number, user: UpdateUser): Observable<null | ErrorResponse> {
		return this.httpClient.put<null>(this.getBasePath(), this.usersService.buildUpdateUserModel(userId, user)).pipe(
			catchError((response: HttpErrorResponse) => {
				return throwError(this.utilsService.buildErrorModel(response));
			})
		);
	}

	deleteUser$(id: number): Observable<null | ErrorResponse> {
		return this.httpClient.post<null>(`${this.getBasePath()}/delete`, { userId: id }).pipe(
			catchError((response: HttpErrorResponse) => {
				return throwError(this.utilsService.buildErrorModel(response));
			})
		);
	}

	lockUser$(userId: number): Observable<null | ErrorResponse> {
		return this.httpClient.post<null>(this.getLockUserPath(), { userId: userId }).pipe(
			catchError((response: HttpErrorResponse) => {
				return throwError(this.utilsService.buildErrorModel(response));
			})
		);
	}

	unlockUser$(userId: number): Observable<null | ErrorResponse> {
		return this.httpClient.post<null>(this.getUnlockUserPath(), { userId: userId }).pipe(
			catchError((response: HttpErrorResponse) => {
				return throwError(this.utilsService.buildErrorModel(response));
			})
		);
	}

	unblockUser$(unblockuserData: UnblockUser): Observable<null | ErrorResponse> {
		return this.httpClient.put<null>(this.getUnblockUserPath(), unblockuserData).pipe(
			catchError((response: HttpErrorResponse) => {
				return throwError(this.utilsService.buildErrorModel(response));
			})
		);
	}

	userExists$(email: string): Observable<boolean> {
		return this.httpClient.get<boolean>(this.userExistsPath(), { params: { Email: email } });
	}

	private getNationalitiesPath(): string {
		return `${environment.apiUrl}/Nationalities/all`;
	}

	private getBasePath(): string {
		return `${environment.apiUrl}/Users`;
	}

	private getIdPath(id: number): string {
		return `${this.getBasePath()}/${id}`;
	}

	private getLockUserPath(): string {
		return `${this.getBasePath()}/lock`;
	}

	private getUnlockUserPath(): string {
		return `${this.getBasePath()}/unlock`;
	}

	private userExistsPath(): string {
		return `${this.getBasePath()}/exists`;
	}

	private getUnblockUserPath(): string {
		return `${environment.apiUrl}/Customer/unblock`;
	}
}
